import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  { src: "/hero/slide-1.webp", alt: "Vedanti Academy — Banking, SSC, Railway & MBA CET coaching in Badlapur" },
  { src: "/hero/slide-2.webp", alt: "Live classes and expert faculty at Vedanti Academy" },
  { src: "/hero/slide-3.webp", alt: "Students preparing for IBPS, SBI, RBI, SSC at Vedanti Academy" },
  { src: "/hero/slide-4.webp", alt: "MBA CET coaching with 99 percentile achievers at Vedanti Academy" },
  { src: "/hero/slide-5.webp", alt: "Railway NTPC & Group D coaching at Vedanti Academy Badlapur" },
  { src: "/hero/slide-6.webp", alt: "LIC, GIC, NIACL Insurance exam coaching at Vedanti Academy" },
];

const INTERVAL_MS = 5000;

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((i) => setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length), []);
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      className="relative w-full h-[440px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="carousel"
      aria-label="Vedanti Academy highlights"
      data-testid="hero-carousel"
    >
      {SLIDES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.alt}
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "auto"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${i === index ? "opacity-100" : "opacity-0"}`}
        />
      ))}

      {/* Subtle bottom gradient for readability of dots */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent"></div>

      {/* Prev / Next */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white flex items-center justify-center transition-all border border-white/20"
        data-testid="hero-carousel-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next slide"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white flex items-center justify-center transition-all border border-white/20"
        data-testid="hero-carousel-next"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2" data-testid="hero-carousel-dots">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === index ? "true" : "false"}
            className={`h-1.5 rounded-full transition-all ${i === index ? "w-7 bg-vedanti-orange" : "w-3 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </div>
  );
};
